import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { motion, animate, useMotionValue, useTransform } from 'framer-motion';
import { medirTinta } from '../../utils/medirTinta';

const buildImageUrl = (url) => {
  if (!url) return null;
  return url.startsWith('http') ? url : `https://${url}`;
};

/*
 * Geometría medida pixel a pixel sobre la referencia y llevada a un viewBox de
 * 576 de ancho para tres pestañas. Todo está en unidades del viewBox, así que
 * escala parejo a cualquier ancho de pantalla.
 *
 *   TOPE_Y         borde superior: la activa y las apagadas a la MISMA altura
 *   BANDA_Y        línea de la banda de la que "nace" la activa
 *   SESGO_ACTIVA   cuánto se abre cada lado de la activa de arriba a abajo
 *                  (pendiente ~0.35)
 *   SESGO_APAGADA  lo mismo en las apagadas, más rectas (pendiente ~0.26)
 *   RADIO          redondeo de las esquinas de arriba
 *   FILETE         curva cóncava con la que la activa se funde con la banda
 *   ANCHO_BASE     ancho de arriba de una pestaña "promedio"; con tres pestañas
 *                  las tres suman lo mismo que antes, solo se reparte distinto
 *   HUECO          separación entre los topes de dos pestañas vecinas
 *   ETIQUETA_ALTO  alto al que se dibuja el lienzo de la etiqueta (todas al
 *                  mismo alto, para que su texto mida igual)
 *   RELLENO        aire a cada lado del texto dentro de la pestaña
 */
const ALTO = 100;
const TOPE_Y = 11;
const BANDA_Y = 89;
const SESGO_ACTIVA = 28;
const SESGO_APAGADA = 20;
const RADIO = 16;
const FILETE = 18;
const ANCHO_BASE = 160;
// Hueco entre topes en el acomodo estándar. Es algo mayor que HUECO_MIN para
// que una etiqueta larga pueda crecer unos pixeles sin que las pestañas se
// junten más que en la referencia.
const HUECO = 18;
const ETIQUETA_ALTO = 57;
const RELLENO = 18;
// Tramo de banda, a cada lado de la pestaña activa, en el que el filo brillante
// se apaga. Medido en el video: no corre a todo lo ancho de la sección.
const DESVANECE = 150;

// Hasta tres pestañas caben en pantalla; de ahí en adelante la barra crece y
// scrollea.
const anchoPara = (n) => 576 + Math.max(0, n - 3) * (ANCHO_BASE + HUECO);

/*
 * Ancho y posición de cada pestaña.
 *
 * Se parte del acomodo estándar: todas de ANCHO_BASE, centradas en la barra,
 * con el mismo hueco en las dos orillas. ANCHO_BASE es el MÍNIMO: ninguna se
 * angosta aunque su texto sea corto. Una etiqueta larga gana hasta EXTRA_MAX.
 *
 * Ese extra crece HACIA ADENTRO: la primera conserva su orilla izquierda y la
 * última su orilla derecha, así la curva con la que la activa baja a la banda
 * se sigue viendo en las dos orillas, igual que con el acomodo estándar. Las de
 * en medio crecen parejo a los dos lados. Si los extras se comieran el hueco
 * entre dos vecinas, se reducen para dejar al menos HUECO_MIN.
 */
const EXTRA_MAX = 9;
// Hueco mínimo entre los topes de dos vecinas, medido en la referencia: a la
// altura de las esquinas equivale a 14 unidades. Antes estaba en 4 y el ancho
// extra de las etiquetas largas lo dejaba en 5: se veían demasiado juntas.
const HUECO_MIN = 14;

function distribuir(n, tintas, ancho) {
  const extras = Array.from({ length: n }, (_, i) => {
    const t = tintas[i];
    if (!t) return 0;
    const necesita = ETIQUETA_ALTO * t.aspecto * t.ancho + RELLENO * 2;
    return Math.min(EXTRA_MAX, Math.max(0, (necesita - ANCHO_BASE) * 0.5));
  });

  // Cuánto crece cada una hacia cada lado.
  const haciaIzq = (i, e) => (n === 1 ? e / 2 : i === 0 ? 0 : i === n - 1 ? e : e / 2);
  const haciaDer = (i, e) => (n === 1 ? e / 2 : i === 0 ? e : i === n - 1 ? 0 : e / 2);

  // Si dos vecinas se comerían el hueco entre sus topes, se recorta todo parejo.
  let factor = 1;
  for (let i = 0; i < n - 1; i++) {
    const comido = haciaDer(i, extras[i]) + haciaIzq(i + 1, extras[i + 1]);
    if (comido > HUECO - HUECO_MIN) factor = Math.min(factor, (HUECO - HUECO_MIN) / comido);
  }

  const total = n * ANCHO_BASE + (n - 1) * HUECO;
  const inicio = (ancho - total) / 2;
  const anchos = [];
  const centros = [];
  for (let i = 0; i < n; i++) {
    const e = extras[i] * factor;
    const estandar = inicio + ANCHO_BASE / 2 + i * (ANCHO_BASE + HUECO);
    anchos.push(ANCHO_BASE + e);
    centros.push(estandar + (haciaDer(i, e) - haciaIzq(i, e)) / 2);
  }
  return { anchos, centros };
}

/*
 * Centro de la parte VISIBLE de la pestaña i, a la altura de la etiqueta.
 *
 * Una pestaña tapa un pedazo de la vecina que tiene detrás: con la primera
 * activa, su lado inclinado se come el borde izquierdo de la segunda, y la
 * segunda a su vez tapa el borde izquierdo de la tercera. Si la etiqueta se
 * centrara en la pestaña completa quedaría corrida hacia la que la tapa; por
 * eso se centra en lo que sí se ve.
 *
 * Una vecina está encima si es la activa o si está más cerca de la activa (el
 * mismo orden con que se pintan).
 */
const ALTURA_ETIQUETA = (TOPE_Y + BANDA_Y) / 2;

function centroVisible(i, activa, centros, anchos) {
  const t = (ALTURA_ETIQUETA - TOPE_Y) / (BANDA_Y - TOPE_Y);
  const medioA = (k) => anchos[k] / 2 + (k === activa ? SESGO_ACTIVA : SESGO_APAGADA) * t;
  const encima = (k) => k === activa || Math.abs(k - activa) < Math.abs(i - activa);

  let izq = centros[i] - medioA(i);
  let der = centros[i] + medioA(i);
  if (i > 0 && encima(i - 1)) izq = Math.max(izq, centros[i - 1] + medioA(i - 1));
  if (i < centros.length - 1 && encima(i + 1)) der = Math.min(der, centros[i + 1] - medioA(i + 1));
  return (izq + der) / 2;
}

// Vector unitario del lado inclinado, de abajo hacia arriba.
function lado(sesgo) {
  const dy = BANDA_Y - TOPE_Y;
  const l = Math.hypot(sesgo, dy);
  return { ux: sesgo / l, uy: dy / l };
}

/*
 * Contorno de la pestaña activa + la banda, como UNA sola figura: la banda
 * corre a lo ancho, sube en curva cóncava al lado inclinado, dobla en la
 * esquina redondeada, cruza arriba y baja igual del otro lado. Por eso se ve
 * como una carpeta que sale del panel y no como una caja pegada encima.
 */
function contornoActiva(cx, medio, ancho, cerrado) {
  const { ux, uy } = lado(SESGO_ACTIVA);
  const izqB = cx - medio - SESGO_ACTIVA;
  const derB = cx + medio + SESGO_ACTIVA;
  const izqA = cx - medio;
  const derA = cx + medio;

  const trazo = [
    `M 0 ${BANDA_Y}`,
    `L ${izqB - FILETE} ${BANDA_Y}`,
    `Q ${izqB} ${BANDA_Y} ${izqB + ux * FILETE} ${BANDA_Y - uy * FILETE}`,
    `L ${izqA - ux * RADIO} ${TOPE_Y + uy * RADIO}`,
    `Q ${izqA} ${TOPE_Y} ${izqA + RADIO} ${TOPE_Y}`,
    `L ${derA - RADIO} ${TOPE_Y}`,
    `Q ${derA} ${TOPE_Y} ${derA + ux * RADIO} ${TOPE_Y + uy * RADIO}`,
    `L ${derB - ux * FILETE} ${BANDA_Y - uy * FILETE}`,
    `Q ${derB} ${BANDA_Y} ${derB + FILETE} ${BANDA_Y}`,
    `L ${ancho} ${BANDA_Y}`,
  ];
  if (cerrado) trazo.push(`L ${ancho} ${ALTO}`, `L 0 ${ALTO}`, 'Z');
  return trazo.join(' ');
}

// Pestaña apagada: trapecio con esquinas de arriba redondeadas, sentado en la
// banda. `cerrada` en false deja fuera el pie, para trazar solo el filo de
// arriba y los lados.
function contornoApagada(cx, medio, cerrada) {
  const { ux, uy } = lado(SESGO_APAGADA);
  const izqA = cx - medio;
  const derA = cx + medio;
  const trazo = [
    `M ${izqA - SESGO_APAGADA} ${BANDA_Y}`,
    `L ${izqA - ux * RADIO} ${TOPE_Y + uy * RADIO}`,
    `Q ${izqA} ${TOPE_Y} ${izqA + RADIO} ${TOPE_Y}`,
    `L ${derA - RADIO} ${TOPE_Y}`,
    `Q ${derA} ${TOPE_Y} ${derA + ux * RADIO} ${TOPE_Y + uy * RADIO}`,
    `L ${derA + SESGO_APAGADA} ${BANDA_Y}`,
  ];
  if (cerrada) trazo.push('Z');
  return trazo.join(' ');
}

/**
 * Barra de pestañas tipo carpeta, dibujada en SVG.
 *
 * Las apagadas son trapecios oscuros fijos; la activa es una figura que se
 * DESLIZA de una posición a otra al cambiar de pestaña (como en el video), y
 * además cambia de ancho si la nueva pestaña es más ancha o más angosta. Los
 * nombres son las imágenes que sube el admin, en sus dos versiones, cruzándose
 * con un fundido.
 *
 * La activa y la banda son TRANSLÚCIDAS: solo se aclara la parte de arriba de
 * la pestaña, y de ahí para abajo se ve el fondo de la sección. Así toda la
 * sección tiene un solo tono de fondo, el de la imagen que sube el admin.
 *
 * Profundidad, medida en la referencia:
 *   - cada pestaña proyecta una sombra corta sobre la que tiene detrás;
 *   - las apagadas tienen una sombra interna del lado que da a la activa;
 *   - las que están a dos o más lugares de la activa son un poco más oscuras.
 */
export default function PromoTabs({ tabs, activa, onChange }) {
  const id = useId().replace(/:/g, '');
  const n = tabs.length;
  const ancho = anchoPara(n);

  // Cada pestaña puede traer DOS imágenes distintas (seleccionada y apagada) y
  // su dibujo no cae en el mismo lugar del lienzo: se miden las dos. La de
  // seleccionada además decide el ancho de la pestaña.
  const etiquetas = tabs.map((t) => buildImageUrl(t.labelActive) || buildImageUrl(t.labelInactive));
  const etiquetasOff = tabs.map((t, i) => buildImageUrl(t.labelInactive) || etiquetas[i]);
  const claveEtiquetas = etiquetas.concat(etiquetasOff).join('|');
  const [tintas, setTintas] = useState([]);
  const [tintasOff, setTintasOff] = useState([]);

  useEffect(() => {
    let vivo = true;
    Promise.all([Promise.all(etiquetas.map(medirTinta)), Promise.all(etiquetasOff.map(medirTinta))])
      .then(([on, off]) => { if (vivo) { setTintas(on); setTintasOff(off); } });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claveEtiquetas]);

  const { anchos, centros } = useMemo(() => distribuir(n, tintas, ancho), [n, tintas, ancho]);

  const cx = useMotionValue(centros[activa]);
  const medio = useMotionValue(anchos[activa] / 2);
  const relleno = useTransform([cx, medio], ([c, m]) => contornoActiva(c, m, ancho, true));
  const filo = useTransform([cx, medio], ([c, m]) => contornoActiva(c, m, ancho, false));
  // El degradado del filo viaja con la pestaña: opaco sobre ella y apagándose
  // a lo largo de la banda.
  const alcance = ANCHO_BASE / 2 + SESGO_ACTIVA + FILETE + DESVANECE;
  const filoX1 = useTransform(cx, (v) => v - alcance);
  const filoX2 = useTransform(cx, (v) => v + alcance);
  const opacoDesde = DESVANECE / (alcance * 2);
  const contenedor = useRef(null);

  // Cambio de pestaña → se desliza. Si lo que cambió fue el reparto (porque se
  // acaban de medir las etiquetas), se acomoda sin animación para no hacer un
  // brinco raro al cargar.
  const activaPrevia = useRef(activa);
  useEffect(() => {
    const destinoCx = centros[activa];
    const destinoMedio = anchos[activa] / 2;
    if (activaPrevia.current === activa) {
      cx.set(destinoCx);
      medio.set(destinoMedio);
      return undefined;
    }
    activaPrevia.current = activa;
    const opciones = { duration: 0.38, ease: [0.32, 0.72, 0, 1] };
    const a = animate(cx, destinoCx, opciones);
    const b = animate(medio, destinoMedio, opciones);
    return () => { a.stop(); b.stop(); };
  }, [activa, centros, anchos, cx, medio]);

  // Con más de tres pestañas la barra scrollea y la elegida se centra. Se mueve
  // SOLO este contenedor: scrollIntoView también desplazaba la página entera
  // hacia los lados y dejaba un hueco blanco a la derecha.
  useEffect(() => {
    const caja = contenedor.current;
    if (!caja || caja.scrollWidth <= caja.clientWidth) return;
    const destino = (centros[activa] / ancho) * caja.scrollWidth - caja.clientWidth / 2;
    caja.scrollTo({ left: Math.max(0, destino), behavior: 'smooth' });
  }, [activa, ancho, centros]);

  // Las apagadas se pintan de la más lejana a la más cercana a la activa: así
  // la vecina queda encima y su sombra cae sobre la de atrás, como en el video.
  const ordenApagadas = tabs
    .map((_, i) => i)
    .filter((i) => i !== activa)
    .sort((a, b) => Math.abs(b - activa) - Math.abs(a - activa));

  return (
    <div ref={contenedor} className="overflow-x-auto scrollbar-hide">
      <div
        className="relative"
        style={{ width: `${(ancho / 576) * 100}%`, aspectRatio: `${ancho} / ${ALTO}` }}
      >
        <svg
          viewBox={`0 0 ${ancho} ${ALTO}`}
          className="absolute inset-0 w-full h-full overflow-visible"
          aria-hidden="true"
        >
          <defs>
            {/* Relleno sólido de la activa y de la banda: aclarado arriba y,
                al llegar a la banda (88% del alto de la figura), exactamente
                el color del panel de productos, para que pestaña, banda y
                panel sean una sola superficie. */}
            <linearGradient id={`activa-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="var(--color-promo-tab-active-from)" />
              <stop offset="0.876" stopColor="var(--color-promo-tab-active-to)" />
            </linearGradient>

            <motion.linearGradient
              id={`filo-${id}`}
              gradientUnits="userSpaceOnUse"
              x1={filoX1}
              x2={filoX2}
              y1="0"
              y2="0"
            >
              <stop offset="0" stopColor="var(--color-promo-tab-edge)" stopOpacity="0" />
              <stop offset={opacoDesde * 0.55} stopColor="var(--color-promo-tab-edge)" stopOpacity="0.35" />
              <stop offset={opacoDesde} stopColor="var(--color-promo-tab-edge)" stopOpacity="1" />
              <stop offset={1 - opacoDesde} stopColor="var(--color-promo-tab-edge)" stopOpacity="1" />
              <stop offset={1 - opacoDesde * 0.55} stopColor="var(--color-promo-tab-edge)" stopOpacity="0.35" />
              <stop offset="1" stopColor="var(--color-promo-tab-edge)" stopOpacity="0" />
            </motion.linearGradient>

            {/* Sombra interna: oscura del lado que da a la activa y desvanecida
                hacia el otro. Una por cada sentido. */}
            <linearGradient id={`interna-izq-${id}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#000" stopOpacity="0.3" />
              <stop offset="0.42" stopColor="#000" stopOpacity="0" />
            </linearGradient>
            <linearGradient id={`interna-der-${id}`} x1="1" y1="0" x2="0" y2="0">
              <stop offset="0" stopColor="#000" stopOpacity="0.3" />
              <stop offset="0.42" stopColor="#000" stopOpacity="0" />
            </linearGradient>

            {/* Filo de las apagadas: medido en la referencia, el lado derecho
                brilla ~37 puntos sobre el relleno y el tope pasa de ~22 a ~37
                de izquierda a derecha. */}
            <linearGradient id={`filo-apagada-${id}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#fff" stopOpacity="0.08" />
              <stop offset="1" stopColor="#fff" stopOpacity="0.27" />
            </linearGradient>

            {/* Sombra que cada apagada proyecta sobre la de atrás. */}
            <filter id={`sombra-${id}`} x="-20%" y="-30%" width="140%" height="160%">
              <feDropShadow dx="0" dy="0" stdDeviation="2.6" floodColor="#000" floodOpacity="0.45" />
            </filter>

            {/* Sombra de la activa: se calcula de una copia y se deja SOLO la
                parte de afuera de la figura, recortada a la línea de la banda.
                Con un drop-shadow normal, el pie de la banda proyectaría una
                raya oscura sobre el panel de productos. */}
            <filter id={`sombra-activa-${id}`} x="-20%" y="-30%" width="140%" height="160%">
              <feGaussianBlur in="SourceAlpha" stdDeviation="2.6" result="borrosa" />
              <feFlood floodColor="#000" floodOpacity="0.45" />
              <feComposite in2="borrosa" operator="in" result="sombra" />
              <feComposite in="sombra" in2="SourceAlpha" operator="out" />
            </filter>

            {/* Todo lo que hace profundidad se corta en la línea de la banda.
                Antes la banda era un verde sólido que tapaba el pie de las
                pestañas; ahora deja ver el fondo, y sin este recorte se
                asomaban el filo inferior y la sombra como una raya horizontal. */}
            <clipPath id={`arriba-${id}`}>
              <rect x={-50} y={-50} width={ancho + 100} height={BANDA_Y + 50} />
            </clipPath>
          </defs>

          <g clipPath={`url(#arriba-${id})`}>
            {ordenApagadas.map((i) => {
              const d = contornoApagada(centros[i], anchos[i] / 2, true);
              const lejos = Math.abs(i - activa) >= 2;
              return (
                <g key={i}>
                  <path d={d} fill="var(--color-promo-tab-inactive)" filter={`url(#sombra-${id})`} />
                  <path d={d} fill={`url(#interna-${i > activa ? 'izq' : 'der'}-${id})`} />
                  {lejos && <path d={d} fill="#000" fillOpacity="0.12" />}
                  {/* Filo claro arriba y a los lados, no en el pie. Va de
                      tenue a la izquierda a más brillante a la derecha: en la
                      referencia el lado derecho de cada apagada tiene una luz
                      leve y el tope se aclara hacia ese lado. */}
                  <path
                    d={contornoApagada(centros[i], anchos[i] / 2, false)}
                    fill="none"
                    stroke={`url(#filo-apagada-${id})`}
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </g>
              );
            })}

            <motion.path d={relleno} fill="#000" filter={`url(#sombra-activa-${id})`} />
          </g>
          <motion.path d={relleno} fill={`url(#activa-${id})`} />
          <motion.path
            d={filo}
            fill="none"
            stroke={`url(#filo-${id})`}
            strokeWidth="2.5"
            strokeLinejoin="round"
          />
        </svg>

        {tabs.map((tab, i) => {
          const esActiva = i === activa;
          const on = etiquetas[i];
          const off = etiquetasOff[i];
          const t = tintas[i];
          // Dónde cae el centro del DIBUJO dentro de cada lienzo (0..1). Se
          // mide por imagen: la versión apagada puede venir acomodada distinto
          // que la seleccionada, y usar la medida de una para la otra corría la
          // apagada unos pixeles a la izquierda.
          const centroDibujo = (m) => (m ? m.x + m.ancho / 2 : 0.5);
          const centroOn = centroDibujo(t);
          const centroOff = centroDibujo(tintasOff[i]);
          // Si el reparto le dejó a la pestaña menos ancho que su texto, la
          // etiqueta se encoge lo justo para no salirse.
          const tintaAncho = t ? ETIQUETA_ALTO * t.aspecto * t.ancho : 0;
          const ajuste = t ? Math.min(1, (anchos[i] - 12) / tintaAncho) : 1;
          const escala = ajuste * (esActiva ? 1 : 0.9);
          // Empujoncito leve alejándose de la pestaña que la tapa (35% de la
          // diferencia entre el centro de la pestaña y el de su parte visible).
          // El ojo juzga el centrado por el tope de la pestaña, que nunca queda
          // tapado, así que el corrimiento completo se veía de más.
          const haciaVisible =
            ((centroVisible(i, activa, centros, anchos) - centros[i]) * 0.35 / (anchos[i] + HUECO)) * 100;

          return (
            <button
              key={tab._id || i}
              type="button"
              data-tab={i}
              onClick={() => onChange(i)}
              aria-label={tab.name || `Pestaña ${i + 1}`}
              aria-pressed={esActiva}
              className="absolute top-0 h-full -translate-x-1/2"
              // Área de toque = ancho de la pestaña más el hueco: las vecinas se
              // tocan sin encimarse.
              style={{
                left: `${(centros[i] / ancho) * 100}%`,
                width: `${((anchos[i] + HUECO) / ancho) * 100}%`,
              }}
            >
              {on ? (
                // Ancla de ancho cero en el punto donde va el centro del
                // dibujo. Cada imagen se recorre para poner el centro de SU
                // dibujo en el ancla, y la escala se aplica alrededor de ese
                // mismo punto, así nada se descentra al crecer o encogerse.
                <span
                  className="absolute w-0 transition-[left] duration-300 ease-out"
                  style={{
                    left: `calc(50% + ${haciaVisible}%)`,
                    top: `${(ALTURA_ETIQUETA / ALTO) * 100 + 1}%`,
                    height: `${(ETIQUETA_ALTO / ALTO) * 100}%`,
                    transform: 'translateY(-50%)',
                  }}
                >
                  <span
                    className="absolute inset-y-0 left-0 w-0 transition-transform duration-300 ease-out"
                    style={{ transform: `scale(${escala})`, transformOrigin: '0 50%' }}
                  >
                    {/* Las dos versiones encimadas, cruzándose con un fundido. */}
                    <img src={on} alt="" loading="lazy"
                      className={`absolute top-0 left-0 h-full w-auto max-w-none transition-opacity duration-300 ${esActiva ? 'opacity-100' : 'opacity-0'}`}
                      style={{ transform: `translateX(-${centroOn * 100}%)` }} />
                    <img src={off} alt="" loading="lazy"
                      className={`absolute top-0 left-0 h-full w-auto max-w-none transition-opacity duration-300 ${esActiva ? 'opacity-0' : 'opacity-100'}`}
                      style={{ transform: `translateX(-${centroOff * 100}%)` }} />
                  </span>
                </span>
              ) : (
                <span className={`text-[12px] font-extrabold uppercase tracking-tight ${esActiva ? 'text-white' : 'text-white/70'}`}>
                  {tab.name || `Pestaña ${i + 1}`}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
